// The default number of guillotine cuts needed for one material sheet, for the three "cut to size" checkboxes in
// the guillotine section. It is only the DEFAULT -- the user can still edit the field. Version-2 (Manufacturing
// Cost Calculator) only.
//
// Each "cutting to size" step guillotines a bigger sheet down into a smaller one:
//   - Pre-print cutting to size: the material sheet cut into printing sheets.
//   - Pre-die-cut cutting to size: the printing sheet cut into die-cutting sheets.
// The count is the grid cut count between the two sheets (see guillotineCount). The backend surfaces it per step
// on sheet_dimension_annotations (already 0 on a bobbin, 2 on an l_shape gang, and routed material->die on a
// die-cutting-only run); where none is surfaced it is computed here from the two sheet sizes. Squaring (trimming
// the sheet's four edges straight) is a flat 4 cuts.

// How many smaller sheets fit along one edge of the bigger sheet (floored). Zero when either size is missing or
// non-positive, so a missing dimension contributes no cuts rather than NaN/Infinity.
const sliceCount = (biggerMm, smallerMm) => {
  if (!(biggerMm > 0) || !(smallerMm > 0)) {
    return 0;
  }
  return Math.floor(biggerMm / smallerMm);
};

// Internal cuts to divide a sheet into countX-wide by countY-tall pieces. Never negative (a single
// piece along an axis needs no cut there).
const gridCuts = (countX, countY) => Math.max(0, countX - 1) + Math.max(0, countY - 1);

// { copies, cuts } for tiling `from` with a `to` sheet of the given width and height. A `to` that does not fit
// tiles zero copies and takes no cuts, so it never wins the best-yield pick unless nothing fits at all.
const tiling = (from, toWidth, toHeight) => {
  const columns = sliceCount(from.width, toWidth);
  const rows = sliceCount(from.height, toHeight);
  const copies = columns * rows;
  return copies === 0 ? { copies: 0, cuts: 0 } : { copies, cuts: gridCuts(columns, rows) };
};

// The number of guillotine cuts to divide the `from` sheet into copies of the `to` sheet: the internal cuts of
// the grid `to` tiles `from` in -- (columns - 1) + (rows - 1). The small sheet is tiled in whichever orientation
// fits the MOST copies (tie-break: fewer cuts), since the shop rotates it freely to minimise waste and this
// function cannot read the solve's chosen rotation. Zero when either sheet ({ width, height }) is missing.
// Mirrors GuillotineCutCount on the backend.
export const guillotineCount = (from, to) => {
  if (!from || !to) {
    return 0;
  }
  const orientations = [tiling(from, to.width, to.height), tiling(from, to.height, to.width)];
  orientations.sort((a, b) => (b.copies - a.copies) || (a.cuts - b.cuts));
  return orientations[0].cuts;
};

// The cuts one "cutting to size" step contributes: the backend cut count when it surfaced one (already 0 on a
// bobbin, 2 on an l_shape gang, routed material->die on a die-cutting-only run), otherwise the grid cut count
// computed here from the two sheet sizes.
const cuttingToSizeCount = (backendCutCount, biggerSheet, smallerSheet) => {
  if (backendCutCount !== null && backendCutCount !== undefined) {
    return backendCutCount;
  }
  return guillotineCount(biggerSheet, smallerSheet);
};

export function guillotineCutCountDefault(options, values) {
  const annotations = (options && options.sheet_dimension_annotations) || {};
  const material = annotations.material_sheet;
  const printing = annotations.printing_layout;
  const dieCutting = annotations.die_cutting_layout;
  const checkboxes = values || {};

  let cutCount = 0;

  if (checkboxes.pre_print_cutting_to_size === true) {
    cutCount += cuttingToSizeCount(annotations.pre_print_cut_count, material, printing);
  }

  if (checkboxes.pre_die_cut_cutting_to_size === true) {
    cutCount += cuttingToSizeCount(annotations.pre_die_cut_cut_count, printing, dieCutting);
  }

  if (checkboxes.squaring === true) {
    cutCount += 4;
  }

  return cutCount;
}
